import React, { useState, useEffect } from 'react';
import { Editor, Toolbar } from '@wangeditor/editor-for-react';
import { IDomEditor, IEditorConfig, IToolbarConfig } from '@wangeditor/editor';
import '@wangeditor/editor/dist/css/style.css';
import styles from './index.module.scss';
import { useAppSelector } from '@/store/hooks';

interface RichTextEditorProps {
  value?: string;
  onChange?: (html: string) => void;
  placeholder?: string;
  height?: number;
  readOnly?: boolean;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value = '',
  onChange,
  placeholder = 'Please enter content...',
  height = 300,
  readOnly = false,
}) => {
  // 编辑器实例
  const [editor, setEditor] = useState<IDomEditor | null>(null);
  // 编辑器内容
  const [html, setHtml] = useState<string>(value);
  // 获取token
  const token = useAppSelector((state) => state.user.token);

  // 工具栏配置
  const toolbarConfig: Partial<IToolbarConfig> = {
    excludeKeys: ['fullScreen'],
    insertKeys: {
      index: 25,
      keys: ['insertTable', 'insertVideo', 'codeBlock', 'divider'],
    },
  };

  // 编辑器配置
  const editorConfig: Partial<IEditorConfig> = {
    placeholder,
    readOnly,
    MENU_CONF: {},
  };

  // 上传图片配置
  editorConfig.MENU_CONF!['uploadImage'] = {
    server: `${process.env.REACT_APP_API_URL}/api/product/save_upload_file`,
    fieldName: 'file',
    maxFileSize: 10 * 1024 * 1024, // 10MB
    maxNumberOfFiles: 10,
    allowedFileTypes: ['image/*'],
    meta: {
      // 自定义上传参数
    },
    headers: {
      token: `${token}`,
    },
    // 自定义上传参数
    formData: {
      // 额外参数
    },
    // 自定义插入图片
    customInsert(res: any, insertFn: Function) {
      // res 即服务端的返回结果
      const url = res.data?.url || '';
      if (url) {
        insertFn(url);
      }
    },
  };

  // 上传视频配置
  editorConfig.MENU_CONF!['uploadVideo'] = {
    server: `${process.env.REACT_APP_API_URL}/api/product/save_upload_file`,
    fieldName: 'file',
    maxFileSize: 100 * 1024 * 1024, // 100MB
    maxNumberOfFiles: 5,
    allowedFileTypes: ['video/*'],
    meta: {
      // 自定义上传参数
    },
    headers: {
      token: `${token}`,
    },
    // 自定义上传参数
    formData: {
      // 额外参数
    },
    // 自定义插入视频
    customInsert(res: any, insertFn: Function) {
      // res 即服务端的返回结果
      const url = res.data?.url || '';
      if (url) {
        insertFn(url);
      }
    },
  };

  // 表格配置
  editorConfig.MENU_CONF!['insertTable'] = {
    // 可配置行列数
    rowNum: 5,
    colNum: 5,
  };

  // 代码块配置
  editorConfig.MENU_CONF!['codeSelectLang'] = {
    codeLangs: [
      { text: 'CSS', value: 'css' },
      { text: 'HTML', value: 'html' },
      { text: 'XML', value: 'xml' },
      { text: 'JavaScript', value: 'javascript' },
      { text: 'TypeScript', value: 'typescript' },
      { text: 'JSX', value: 'jsx' },
      { text: 'JSON', value: 'json' },
      { text: 'Python', value: 'python' },
      { text: 'Java', value: 'java' },
      { text: 'C', value: 'c' },
      { text: 'C++', value: 'cpp' },
      { text: 'Go', value: 'go' },
      { text: 'Markdown', value: 'markdown' },
      { text: 'Plain Text', value: 'text' },
    ]
  };

  // 初始化值
  useEffect(() => {
    setHtml(value);
  }, [value]);

  // 及时销毁编辑器
  useEffect(() => {
    return () => {
      if (editor == null) return;
      editor.destroy();
      setEditor(null);
    };
  }, [editor]);

  // 内容变化时触发
  const handleChange = (editor: IDomEditor) => {
    const html = editor.getHtml();
    setHtml(html);
    onChange && onChange(html);
  };

  return (
    <div className={styles['rich-text-editor']}>
      <div className={styles['editor-container']} style={{ height: `${height}px`, overflow: 'hidden' }}>
        <Toolbar
          editor={editor}
          defaultConfig={toolbarConfig}
          mode="default"
          style={{ borderBottom: '1px solid #e8e8e8' }}
        />
        <Editor
          defaultConfig={editorConfig}
          value={html}
          onCreated={setEditor}
          onChange={handleChange}
          mode="default"
          style={{ height: `${height - 50}px`, overflowY: 'auto' }}
        />
      </div>
    </div>
  );
};

export default RichTextEditor; 